import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material"
import { PieChart } from "@mui/x-charts"

import type { PiePlotView, RecordType } from "types/dashboard.types"
import type { QualityDescriptionParams, QualityRecord } from "types/quality.types"
import QualityDialog from "./QualityDialog"

interface QualityMiniPieProps {
  qualityData: QualityRecord[]
  descriptionParams?: QualityDescriptionParams
}

const MINI_SIZE = 20

const QualityMiniPie = ({ qualityData, descriptionParams }: QualityMiniPieProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const [open, setOpen] = useState(false)

  const nonZeroQualities = qualityData.filter((q) => q.percentage > 0)

  const pieData = [...nonZeroQualities]
    .sort((a, b) => b.percentage - a.percentage)
    .map((q) => ({
      id: q.name,
      value: q.percentage,
      label: t(`quality.${q.name.toUpperCase()}`),
      color: theme.palette.quality[q.name].main,
    }))

  const pieView = useMemo<PiePlotView>(
    () => ({
      type: "pie",
      name: t("quality.DATA_QUALITY"),
      nameKey: "quality.DATA_QUALITY",
      properties: {
        decimalPlaces: 1,
        prefix: "",
        suffix: "%",
        colors: nonZeroQualities.map((q, i) => ({
          value: i + 1,
          hex: theme.palette.quality[q.name].main,
        })),
      },
    }),
    [nonZeroQualities, theme, t],
  )

  const pieRecords = useMemo<RecordType[]>(
    () =>
      nonZeroQualities.map((q, i) => ({
        nameKey: `quality.${q.name.toUpperCase()}`,
        _value: Number(q.percentage.toFixed(1)),
        colorKey: i + 1,
        order: i,
      })),
    [nonZeroQualities],
  )

  if (nonZeroQualities.length === 0) return null

  const tooltipTitle = (
    <Stack gap={0.5}>
      <Typography variant="caption" sx={{ fontWeight: 600 }}>
        {t("quality.DATA_QUALITY")}
      </Typography>
      {nonZeroQualities.map((q) => (
        <Typography key={q.name} variant="caption">
          {t(`quality.${q.name.toUpperCase()}`)}: {q.percentage.toFixed(1)}%
        </Typography>
      ))}
    </Stack>
  )

  return (
    <>
      <Tooltip title={tooltipTitle} enterTouchDelay={0} placement="bottom" arrow>
        <Box
          data-testid="quality-mini-pie"
          onClick={() => setOpen(true)}
          sx={{
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            transition: "transform 0.2s ease-in-out",
            "&:hover": {
              transform: "scale(1.1)",
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: MINI_SIZE,
              height: MINI_SIZE,
              borderRadius: "50%",
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
            }}
          >
            <PieChart
              width={MINI_SIZE}
              height={MINI_SIZE}
              series={[
                {
                  data: pieData,
                },
              ]}
              hideLegend
              skipAnimation
              margin={0}
              sx={{
                pointerEvents: "none",
                "& .MuiPieArc-root": { stroke: "none" },
              }}
            />
            {nonZeroQualities.length === 1 && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: "50%",
                  left: "50%",
                  width: "1.5px",
                  height: "50%",
                  backgroundColor: theme.palette.divider,
                }}
              />
            )}
          </Box>
        </Box>
      </Tooltip>

      <QualityDialog
        open={open}
        onClose={() => setOpen(false)}
        descriptionParams={descriptionParams}
        nonZeroQualities={nonZeroQualities}
        pieView={pieView}
        pieRecords={pieRecords}
      />
    </>
  )
}

export default QualityMiniPie
